import { z } from 'zod';

/**
 * Wave 11 — Project budget (orçamento).
 *
 * A project is a flat list of `BudgetItem`s. Each item has a category,
 * description, and planned cost. Actuals are derived from approved POs
 * tagged with the item (see `budget.service.ts` on the API). The client
 * never computes variance — the server returns `actualCents` alongside
 * `plannedCents` so the single source of truth stays on the API.
 *
 * Categories match how Brazilian construction shops talk about costs:
 *   obra        — structural / construction work
 *   material    — raw materials (cimento, aço, etc.)
 *   mao_obra    — direct labor (pedreiro, ajudante)
 *   servico     — subcontracted services (hidráulica, elétrica)
 *   equipamento — rented equipment (andaime, betoneira)
 *   outros      — escape hatch so the dropdown never blocks save
 */

export const budgetCategorySchema = z.enum([
  'obra',
  'material',
  'mao_obra',
  'servico',
  'equipamento',
  'outros',
]);
export type BudgetCategory = z.infer<typeof budgetCategorySchema>;

// Wave 379J · provenance of a budget_item row.
//   manual       — created via the "Novo item" form (or any non-bulk path)
//   imported     — created in batch via the xlsx import endpoint
// Wave 92 F1 · 2026-05-24 · IA imputation lifecycle:
//   ia_estimate  — gerado pela imputação IA pós-finalize (badge ✱)
//   pm_input     — PM editou linha estimada → vira input do PM (badge ✓)
//   out_of_scope — PM marcou linha mandatória "fora de escopo" (badge —)
export const budgetItemSourceSchema = z.enum([
  'manual',
  'imported',
  'ia_estimate',
  'pm_input',
  'out_of_scope',
]);
export type BudgetItemSource = z.infer<typeof budgetItemSourceSchema>;

// Wave 92 F1 · provenance da imputação IA (qual fonte gerou o valor).
export const budgetItemImputedFromSchema = z.enum([
  'analogical_median',
  'sanity_range_p50',
  'bucket_subitem',
  'llama_fallback',
]);
export type BudgetItemImputedFrom = z.infer<typeof budgetItemImputedFromSchema>;

// Wave 92 F1 · faixa de confiança da estimativa IA.
export const budgetItemImputedConfidenceSchema = z.enum(['low', 'medium', 'high']);
export type BudgetItemImputedConfidence = z.infer<typeof budgetItemImputedConfidenceSchema>;

export const budgetItemSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  projectId: z.string(),
  category: budgetCategorySchema,
  description: z.string(),
  plannedCents: z.number().int().nonnegative(),
  sortOrder: z.number().int(),
  // Wave 379H · hierarchical reference code from BR construction
  // budgets (REFER "01.01" / ICO "1.7"). Optional; pre-379H rows
  // hold NULL.
  ref: z.string().nullable().optional(),
  // Wave 379J · provenance flag. Defaults to 'manual' on legacy rows
  // and on direct create. The xlsx import endpoint stamps 'imported'.
  source: budgetItemSourceSchema.default('manual'),
  // Wave 379L · per-row import diagnostic. Set only by xlsx import
  // when the auto-importer couldn't fully resolve the row (outlier,
  // duplicate, missing ref, category fallback). UI shows a ⚠ badge
  // with this string on hover so PM can review the row in place
  // instead of having to re-run the import.
  importWarning: z.string().nullable().optional(),
  // Wave 379N · canonical unidade de medida (vb/un/cj/m/ml/m2/m3/
  // kg/t/l/h/dia/mes/caminhao/cacamba/br). Free-text TEXT column on
  // the API side so we don't have to migrate for new units; the client
  // owns the alias map (`apps/web/src/lib/budget-units.ts`) and falls
  // back to 'vb' when the spreadsheet has something unknown.
  unit: z.string().nullable().optional(),
  // Wave 379AS · quantidade × preço unitário = total. PM lança custo
  // com qty + unit_price; server calcula `total_cents = round(qty *
  // unit_price_cents)` e espelha em `plannedCents` pra back-compat.
  // Linhas legadas (importadas pré-379AS) continuam com qty/unit_price
  // NULL — a UI mostra apenas o total nesse caso.
  quantity: z.number().nonnegative().nullable().optional(),
  unitPriceCents: z.number().int().nonnegative().nullable().optional(),
  totalCents: z.number().int().nonnegative().nullable().optional(),
  // Wave 379S · "marcar pra comprar". Quando true, o item aparece no
  // Backlog da página /compras. Sai do backlog automaticamente quando
  // entra em cotação aberta ou em PO ativo (derivado server-side).
  toPurchase: z.boolean().default(false),
  purchaseIntentAt: z.string().nullable().optional(),
  // Wave pm-chapters · 3-level chapter hierarchy (migration 0222).
  // NULL on rows created before this wave.
  chapterLevel1: z.string().nullable().optional(),
  chapterLevel2: z.string().nullable().optional(),
  chapterLevel3: z.string().nullable().optional(),
  chapterLabel: z.string().nullable().optional(),
  // Wave 92 F1 · provenance da imputação IA (NULL pra source != 'ia_estimate').
  imputedFrom: budgetItemImputedFromSchema.nullable().optional(),
  imputedConfidence: budgetItemImputedConfidenceSchema.nullable().optional(),
  imputedAt: z.string().nullable().optional(),
  imputedRangeMinCents: z.number().int().nonnegative().nullable().optional(),
  imputedRangeMaxCents: z.number().int().nonnegative().nullable().optional(),
  // Wave 131 · P2 — link reverso à etapa (work_stages.id). NULL quando
  // o item ainda não foi associado a nenhuma etapa via gerador de etapas.
  stageId: z.string().nullable().optional(),
  // Wave 92 F2 · justificativa quando PM zera linha mandatória.
  outOfScopeReason: z.string().nullable().optional(),
  archivedAt: z.string().nullable(),
  deletedAt: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type BudgetItem = z.infer<typeof budgetItemSchema>;

/**
 * Projection returned by the summary endpoint — adds the server-computed
 * actuals + a convenience variance string. The client uses variance to
 * colour the row; no math on the SPA.
 */
export const budgetItemWithActualSchema = budgetItemSchema.extend({
  actualCents: z.number().int().nonnegative(),
  poCount: z.number().int().nonnegative(),
  /** `actual / planned` as a percentage integer, 0..N (can exceed 100). */
  usagePercent: z.number().int().nonnegative(),
});
export type BudgetItemWithActual = z.infer<typeof budgetItemWithActualSchema>;

export const budgetCategoryTotalSchema = z.object({
  category: budgetCategorySchema,
  plannedCents: z.number().int().nonnegative(),
  actualCents: z.number().int().nonnegative(),
  itemCount: z.number().int().nonnegative(),
});
export type BudgetCategoryTotal = z.infer<typeof budgetCategoryTotalSchema>;

export const budgetSummarySchema = z.object({
  items: z.array(budgetItemWithActualSchema),
  totalsByCategory: z.array(budgetCategoryTotalSchema),
  grandPlannedCents: z.number().int().nonnegative(),
  grandActualCents: z.number().int().nonnegative(),
  /** POs with no budget_item_id attached, rolled up for the "sem categoria" row. */
  untaggedActualCents: z.number().int().nonnegative(),
  untaggedPoCount: z.number().int().nonnegative(),
});
export type BudgetSummary = z.infer<typeof budgetSummarySchema>;

export const createBudgetItemRequestSchema = z.object({
  category: budgetCategorySchema,
  description: z.string().trim().min(2).max(200),
  // Wave 379J · createBudgetItem (manual / form path) keeps the strict
  // R$ 100M cap because the form submits one row at a time — a Zod
  // 400 there is appropriate user feedback. Bulk import uses the
  // looser schema in importBudgetItemsRequestSchema so one bad row
  // doesn't kill the whole batch (per-row reason surfacing instead).
  plannedCents: z.number().int().nonnegative().max(10_000_000_000), // R$ 100M per row
  sortOrder: z.number().int().optional(),
  // Wave 379H · hierarchical reference (REFER from BR orçamento xlsx,
  // e.g. "01.01"). Persisted to budget_items.ref; later resolved by
  // ICO matching when a quotation closes into a PO.
  ref: z.string().trim().max(32).optional(),
  // Wave 379J · provenance. Internal callers (xlsx import) set
  // 'imported'; the public create endpoint defaults to 'manual'.
  source: budgetItemSourceSchema.optional(),
  // Wave 379L · per-row import warning string. Forwarded by the import
  // route from the per-row diagnostic; optional + nullable on direct
  // create.
  importWarning: z.string().trim().max(240).nullish(),
  // Wave 379N · unidade de medida canônica.
  unit: z.string().trim().max(32).nullish(),
  // Wave 379AS · quantidade × preço unitário (opcional — manual ou
  // import legado deixam vazio). Quando presentes, server recalcula
  // total_cents e espelha em planned_cents.
  quantity: z.number().nonnegative().nullish(),
  unitPriceCents: z.number().int().nonnegative().nullish(),
  // Wave 379S · marca o item pra entrar no Backlog do /compras.
  toPurchase: z.boolean().optional(),
  // Wave pm-chapters · capítulo hierárquico no formato 'N.M.P'.
  // Opcional no create — PM pode classificar depois.
  chapterCode: z.string().trim().max(32).nullish(),
  chapterLabel: z.string().trim().max(200).nullish(),
});
export type CreateBudgetItemRequest = z.infer<typeof createBudgetItemRequestSchema>;

// ObraFit Module 3: batch import (Excel/CSV → JSON via SheetJS in browser).
// Server validates each row + accumulates skip counts for empty/zero rows.
//
// Wave 379I+J · schema cap at the upper safety bound (max safe int /
// 100 ≈ R$ 90 quadrillion). The actual per-row "is this realistic"
// check lives in wave11.ts at R$ 100M and surfaces as a `tooLarge`
// reason in the outcome banner. Reason for the split: a single rogue
// value (decimal parse bug, paste accident) shouldn't kill the whole
// 500-row import with a 400 ZodError — the user gets nothing useful
// back. The loose schema accepts the payload, the per-row guard skips
// the bad rows, and the outcome banner tells PM exactly which rows
// failed and why.
export const importBudgetItemsRequestSchema = z.object({
  projectId: z.string().min(1),
  items: z.array(z.object({
    category: budgetCategorySchema,
    description: z.string().trim().max(240),
    // Loose cap: Number.MAX_SAFE_INTEGER, just to keep Zod's int check
    // honest. Per-row realism check is in the route handler.
    plannedCents: z.number().int().nonnegative(),
    ref: z.string().trim().max(32).optional(),
    // Wave 379L · per-row diagnostic from the auto-importer. Optional
    // string surfaced as a ⚠ badge on the budget row so PM can review
    // the few flagged rows instead of re-running the whole import.
    importWarning: z.string().trim().max(240).optional(),
    // Wave 379N · unidade de medida canônica (já normalizada client-side).
    unit: z.string().trim().max(32).optional(),
  })).min(1).max(2000),
});
export type ImportBudgetItemsRequest = z.infer<typeof importBudgetItemsRequestSchema>;

export const updateBudgetItemRequestSchema = z.object({
  category: budgetCategorySchema.optional(),
  description: z.string().trim().min(2).max(200).optional(),
  plannedCents: z.number().int().nonnegative().max(1_000_000_00_00).optional(),
  sortOrder: z.number().int().optional(),
  archived: z.boolean().optional(),
  // Wave 379L · ref now editable post-create. The manual creation form
  // builds it from the WBS chapter+sub picker; the edit form lets PM
  // re-classify a row that landed in the wrong chapter on import.
  // Empty string = "clear the ref" (persist NULL).
  ref: z.string().trim().max(32).nullish(),
  // Wave 379L · clear the import_warning when PM reviews a flagged row.
  // Sending null wipes the badge.
  importWarning: z.string().trim().max(240).nullish(),
  // Wave 379N · unidade de medida — editável post-create.
  unit: z.string().trim().max(32).nullish(),
  // Wave 379AS · qty + unit_price editáveis. Quando ambos vêm, server
  // recalcula total_cents/planned_cents.
  quantity: z.number().nonnegative().nullish(),
  unitPriceCents: z.number().int().nonnegative().nullish(),
  // Wave 379S · toggle "marcar pra comprar". Server estampa
  // purchase_intent_at sozinho quando o flag passa de false→true.
  toPurchase: z.boolean().optional(),
  // Wave pm-chapters · chapter code editável post-create.
  chapterCode: z.string().trim().max(32).nullish(),
  chapterLabel: z.string().trim().max(200).nullish(),
});
export type UpdateBudgetItemRequest = z.infer<typeof updateBudgetItemRequestSchema>;
